import PersonContact from "features/contacts/blocks/PersonContact";
import type { ContactStatus } from "features/contacts/blocks/PersonContact/PersonContact.types";
import type {
  ContactsListApiResponse,
  ContactsManageApiResponse,
  ContactsManageRequest,
  ErrorResponse,
} from "features/contacts/data/types";
import { isErrorResponse } from "features/contacts/data/utils";
import { useDataQuery } from "hooks/useData";
import { useUser } from "hooks/useUser";
import React, { useEffect, useMemo, useState } from "react";

import { apiHost } from "../../../../consts";

import { detectStatus, getStatusLabel } from "./ContactRequest.utils";

type ContactRequestProps = {
  targetPersonId: number;
};

const ContactRequest: React.FC<ContactRequestProps> = ({ targetPersonId }) => {
  const { isLoggedIn, personId } = useUser();
  const contactsData = useDataQuery<ContactsListApiResponse>(
    "contacts/list/",
    !isLoggedIn,
    null,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ContactStatus>("none");

  const statusFromApi = useMemo(() => {
    if (!contactsData || isErrorResponse(contactsData))
      return "none" as ContactStatus;

    return detectStatus(contactsData, targetPersonId);
  }, [contactsData, targetPersonId]);

  useEffect(() => {
    setStatus(statusFromApi);
  }, [statusFromApi]);

  const sendRequest = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const body: ContactsManageRequest = {
        person_id: targetPersonId,
        action: "add",
      };
      const response = await fetch(
        `${apiHost}/solutions/api/contacts/manage/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        },
      );
      const data: ContactsManageApiResponse = await response.json();

      if (!response.ok || "error" in data) {
        throw new Error((data as ErrorResponse).error || "Request failed");
      }
      setStatus((prev) => (prev === "their_request" ? "mutual" : "my_request"));
    } catch (e: any) {
      setError(e?.message || "Unknown error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoggedIn || !personId || personId === targetPersonId) return null;
  if (!contactsData)
    return (
      <div style={{ marginTop: 12, color: "#666" }}>
        Проверка статуса контакта...
      </div>
    );
  if (isErrorResponse(contactsData))
    return (
      <div style={{ marginTop: 12, color: "#c00" }}>{contactsData.error}</div>
    );

  if (status === "none") {
    return (
      <div style={{ marginTop: 12 }}>
        <button
          onClick={sendRequest}
          disabled={isLoading}
        >
          {isLoading ? "Отправка..." : "Отправить запрос в контакты"}
        </button>
        {error && <div style={{ marginTop: 6, color: "#c00" }}>{error}</div>}
      </div>
    );
  }

  if (status === "their_request") {
    return (
      <div style={{ marginTop: 12 }}>
        <PersonContact
          personId={targetPersonId}
          contactStatus="their_request"
          onActionDone={(nextStatus) => setStatus(nextStatus)}
        />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, color: "#555" }}>
      {getStatusLabel(status)}
      {error && <div style={{ marginTop: 6, color: "#c00" }}>{error}</div>}
    </div>
  );
};

export default ContactRequest;
